import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiCheckbox,
  MiDetailFields,
  MiIcon,
  MiIconButton,
  MiList,
  MiModal,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  CONTACT_DISPLAY_TYPE,
  INPUT_TYPES,
  MODAL_TITLES,
} from "@miplatform/constants";
import { useMiContactRelationshipsModalViewModel } from "@miplatform/view-models";
import { mdiAccountGroupOutline, mdiTrashCanOutline } from "@mdi/js";
import { Divider } from "@mui/material";
import { Box } from "@mui/system";

/**
 * MiContactRelationshipsModal component for managing contact relationships.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Determines whether the modal is open.
 * @param {boolean} props.isLoading - Indicates if the parent component is loading.
 * @param {Object} [props.contact={}] - The contact object containing contact information.
 * @param {boolean} props.isPrimaryDisabled - Disables the option to mark relationships as primary if true.
 * @param {Object} [props.contactCategories={}] - Mapping of contact levels to their corresponding categories and options.
 * @param {Object|null} [props.referenceIds=null] - Mapping of contact levels to their corresponding reference IDs.
 * @param {Array} [props.relationships=contact?.relationships] - List of contact relationships to display.
 * @param {boolean} props.isContactLevelReadOnly - Makes the contact level field read-only if true.
 * @param {string} props.defaultContactLevel - The default contact level for new relationships.
 * @param {Array} props.contactLevelOptions - List of contact level options for selection.
 * @param {Function} props.onRefresh - Function to refresh data after creating, updating, or deleting relationships.
 * @param {Function} props.onClose - Function to close the modal.
 *
 * @returns {JSX.Element} The rendered contact relationships modal component.
 *
 * @example
 * <MiContactRelationshipsModal
 *   open={isModalOpen}
 *   isLoading={isLoading}
 *   contact={contactData}
 *   isPrimaryDisabled={false}
 *   contactCategories={contactCategoriesData}
 *   referenceIds={referenceIdsData}
 *   relationships={relationshipsData}
 *   isContactLevelReadOnly={true}
 *   defaultContactLevel={CONTACT_TYPES.COMMUNITIES}
 *   contactLevelOptions={contactLevelOptionsData}
 *   onRefresh={handleRefresh}
 *   onClose={handleCloseModal}
 * />
 */
export const MiContactRelationshipsModal = ({
  open,
  isLoading: isCardLoading,
  contact = {},
  isPrimaryDisabled,
  contactCategories,
  referenceIds = null,
  relationships = contact?.relationships,
  isContactLevelReadOnly,
  defaultContactLevel,
  onRefresh,
  onClose,
}) => {
  const { theme, classes } = useStyle();
  const {
    isLoading,
    isDeleteModalOpen,
    contactLevelOptions,
    relationshipEdit,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleAddRelationship,
    handleDeleteRelationship,
    handleUpdateRelationship,
    handleResetEdit,
  } = useMiContactRelationshipsModalViewModel({
    contact,
    referenceIds,
    onRefresh,
    defaultContactLevel,
  });
  const relationshipFields = [
    {
      label: "Contact Level",
      name: "level",
      fieldType: INPUT_TYPES.SELECT,
      required: true,
      options: contactLevelOptions,
      isReadOnly: isContactLevelReadOnly,
      handleChange: (e) =>
        relationshipEdit.update({
          isPrimary: false,
          label: "",
          level: e.target.value,
        }),
    },
    {
      label: "Relationship Type",
      name: "label",
      fieldType: "select",
      options:
        contactCategories[relationshipEdit.getValue("level")]?.options || [],
      required: true,
      handleChange: (e) =>
        relationshipEdit.update({
          isPrimary: false,
          label: e.target.value,
        }),
    },
    {
      label: "Primary",
      name: "isPrimary",
      fieldType: "checkbox",
      readOnly: !contactCategories[
        relationshipEdit.getValue("level")
      ]?.primaryRelationshipTypes.has(relationshipEdit.getValue("label")),
    },
  ];

  const relationshipSections = [
    {
      fieldset: [
        {
          field: "level",
          label: "Contact Level",
          renderValue: ({ level }) => CONTACT_DISPLAY_TYPE[level],
        },
        {
          field: "label",
          label: "Relationship Type",
        },
        {
          field: "isPrimary",
          renderValue: (item) => (
            <MiCheckbox
              label="Primary"
              value={item.isPrimary}
              disabled={
                isPrimaryDisabled ||
                !contactCategories[item.level]?.primaryRelationshipTypes.has(
                  item.label
                ) ||
                isLoading
              }
              handleChange={({ target: { checked } }) =>
                handleUpdateRelationship({ ...item, isPrimary: checked })
              }
            />
          ),
        },
      ],
    },
  ];

  const handleClose = () => {
    handleResetEdit();
    onClose();
  };

  const renderItemActions = (item) => (
    <MiIconButton
      className={classes.deleteButton}
      disabled={isLoading}
      onClick={() => handleOpenDeleteModal(item)}
    >
      <MiIcon path={mdiTrashCanOutline} />
    </MiIconButton>
  );

  return (
    <MiModal
      open={open}
      loading={isLoading || isCardLoading}
      width={500}
      title={`${contact.firstName} ${contact.lastName} - Manage Relationships`}
      titleIcon={mdiAccountGroupOutline}
      closeModal={handleClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
      ]}
    >
      <Box className={classes.container}>
        <Box className={classes.list}>
          <MiList
            hideListPagination
            viewType="card"
            cardProps={{ sections: relationshipSections, renderItemActions }}
            data={relationships}
          />
        </Box>
        <Divider sx={{ mt: 2, mb: 2 }} />
        <MiDetailFields
          detailFields={relationshipFields}
          edit={relationshipEdit}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <MiButton
            title="Add Relationship"
            disabled={isCardLoading || isLoading || !relationshipEdit.isValid()}
            variant={BUTTON_VARIANTS.CONTAINED}
            color={BUTTON_COLORS.PRIMARY}
            onClick={handleAddRelationship}
          />
        </Box>
        <Divider sx={{ mt: 2 }} />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          titleIcon={{
            path: mdiTrashCanOutline,
            color: theme.palette.error.main,
          }}
          description="Are you sure you want to delete this relationship?"
          open={isDeleteModalOpen}
          handleNegative={handleCloseDeleteModal}
          handlePositive={handleDeleteRelationship}
        />
      </Box>
    </MiModal>
  );
};

const useStyle = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
  },
  list: {
    maxHeight: 300,
    overflow: "auto",
  },
  deleteButton: {
    color: theme.palette.error.main,
    transition: "color 300ms",
    "&:disabled": {
      color: theme.palette.grey.accent,
    },
    "&:hover": {
      color: theme.palette.error.light,
    },
  },
}));
